import { useLanguage } from "../../../contexts/LanguageContext";
import { formatNumber } from "../format";
import { Panel, Skeleton } from "./ui";

// Earned, redeemed and expired as separate rows: before this, "Points
// redeemed" summed every negative transaction, expiries included.
export default function PointsPanel({ points, rangeDays, loading }) {
  const { t, language } = useLanguage();

  const rows = [
    { key: "earned", color: "bg-indigo-600", value: points?.earned, sign: "+" },
    { key: "redeemed", color: "bg-orange-600", value: points?.redeemed, sign: "−" },
    { key: "expired", color: "bg-gray-400", value: points?.expired, sign: "−" },
  ];
  const total = rows.reduce((sum, r) => sum + Math.abs(r.value || 0), 0);
  const adjusted = points?.adjusted;

  return (
    <Panel
      title={t("dashboard.overview.points.title")}
      subtitle={t("dashboard.range.last", { count: rangeDays })}
      className="flex-[1_1_300px]"
    >
      {loading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-2.5 w-full rounded-full" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="h-5 w-full" />
          <Skeleton className="mt-4 h-12 w-40" />
        </div>
      ) : (
        <>
          <div
            className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-gray-100"
            aria-hidden="true"
          >
            {total > 0 &&
              rows.map((r) =>
                r.value ? (
                  <div
                    key={r.key}
                    className={r.color}
                    style={{ flex: `${Math.abs(r.value)} 1 0` }}
                  />
                ) : null,
              )}
          </div>
          <dl className="flex flex-col gap-3.5">
            {rows.map((r) => (
              <div key={r.key} className="flex items-center justify-between gap-3">
                <dt className="inline-flex items-center gap-2.5 text-sm text-gray-700">
                  <span className={`h-2.5 w-2.5 rounded-[3px] ${r.color}`} aria-hidden="true" />
                  {t(`dashboard.overview.points.${r.key}`)}
                </dt>
                <dd className="text-[15px] font-semibold text-gray-900">
                  {r.value ? r.sign : ""}
                  {formatNumber(Math.abs(r.value || 0), language)}
                </dd>
              </div>
            ))}
            {Number.isFinite(adjusted) && adjusted !== 0 && (
              <div className="flex items-center justify-between gap-3">
                <dt className="pl-5 text-sm text-gray-600">
                  {t("dashboard.overview.points.adjusted")}
                </dt>
                <dd className="text-[15px] font-semibold text-gray-900">
                  {adjusted > 0 ? "+" : "−"}
                  {formatNumber(Math.abs(adjusted), language)}
                </dd>
              </div>
            )}
          </dl>
          <div className="mt-auto flex flex-col gap-1 border-t border-gray-100 pt-4">
            <span className="text-[13px] text-gray-500">
              {t("dashboard.overview.points.outstanding")}
            </span>
            <span className="text-2xl font-semibold tracking-tight text-gray-900">
              {t("dashboard.overview.points.pts", {
                value: formatNumber(points?.outstanding_balance ?? 0, language),
              })}
            </span>
            <span className="text-[13px] text-gray-500">
              {t("dashboard.overview.points.heldBy", {
                count: points?.customers_with_balance ?? 0,
                value: formatNumber(points?.customers_with_balance ?? 0, language),
              })}
            </span>
          </div>
        </>
      )}
    </Panel>
  );
}
